import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, Empty, Input, Modal, Progress, Row, Segmented, Space, Spin, Tag, Tree, Typography, message,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import { ArrowLeftOutlined, CheckCircleFilled } from '@ant-design/icons';
import {
  addressVisitFlag, deleteVisitEvidence, flagVisitPoint, getContractors, getVisitChecklist,
  putVisitAnswer, removeVisitFlag, setVisitScope, undoAddressVisitFlag, uploadVisitEvidence,
} from '../../services/api';
import { getApiError } from '../../utils/apiError';
import {
  Area, AnswerValue, NcSetup, Question, activeQuestions, collectPoints, findArea, isQuestion, needsAttention,
  patchQuestion, progressOf, questionsUnder,
} from './checklistTypes';
import NCDrawer from './NCDrawer';
import QuestionRow from './QuestionRow';
import ScopeModal from './ScopeModal';
import ClosingPanel from './ClosingPanel';
import HistoryDrawer from './HistoryDrawer';
import ResultPanel from './ResultPanel';
import ReviewActions from './ReviewActions';
import ReviewChangeModal from './ReviewChangeModal';

const { Text, Title } = Typography;

const PAGE = 50;
type View = 'area' | 'attention' | 'flagged';

interface VisitInfo {
  visit_id: string; status: string; my_roles: string[]; remarks: string; meeting_open: boolean;
}

interface Props {
  visitId: string;
  onBack: () => void;
}

const ChecklistScreen: React.FC<Props> = ({ visitId, onBack }) => {
  const [visit, setVisit] = useState<VisitInfo | null>(null);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<View>('area');
  const [viewChosen, setViewChosen] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const order = useRef<{ [areaId: string]: string[] }>({});

  const [ncSetup, setNcSetup] = useState<NcSetup | null>(null);
  const [contractors, setContractors] = useState<{ id: string; name: string }[]>([]);
  const [ncFor, setNcFor] = useState<Question | null>(null);
  const [scopeFor, setScopeFor] = useState<Area | null>(null);
  const [closingOpen, setClosingOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [reviewFor, setReviewFor] = useState<Question | null>(null);
  const [reopenFor, setReopenFor] = useState<Area | null>(null);
  const [flagFor, setFlagFor] = useState<Question | null>(null);
  const [flagNote, setFlagNote] = useState('');
  const [historyFor, setHistoryFor] = useState<Question | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) { setLoading(true); order.current = {}; }
    try {
      const res = await getVisitChecklist(visitId);
      setVisit(res.data.visit);
      setAreas(res.data.checklist);
      setNcSetup(res.data.nc_setup || null);
      setError('');
    } catch (err) {
      setError(getApiError(err, 'Could not load the checklist.'));
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    load();
    getContractors().then((res) => setContractors(res.data)).catch(() => undefined);
  }, [load]);

  const isLead = !!visit?.my_roles.includes('LEAD_AUDITOR');
  const isAuditor = isLead || !!visit?.my_roles.includes('AUDITOR');
  const isReviewer = !!visit?.my_roles.includes('REVIEWER');
  const status = visit?.status;
  const reviewing = isReviewer && status === 'UNDER_REVIEW';
  const auditorPhase = isAuditor && (status === 'OPEN' || status === 'RETURNED');

  // After a return the auditors may change only the points the reviewer flagged (and not yet addressed).
  const pointEditable = (q: Question) => {
    if (!isAuditor) return false;
    if (status === 'OPEN') return true;
    return status === 'RETURNED' && q.flag?.status === 'OPEN';
  };

  const flaggedPoints = useMemo(() => collectPoints(areas, (q) => !!q.flag)
    .sort((a, b) => Number(b.q.flag?.status === 'OPEN') - Number(a.q.flag?.status === 'OPEN')), [areas]);
  const attentionPoints = useMemo(() => collectPoints(areas, needsAttention), [areas]);
  const openFlags = flaggedPoints.filter((p) => p.q.flag?.status === 'OPEN').length;
  const showAttention = status === 'OPEN' && isAuditor;

  // Pick the starting view once: flagged points first for whoever has to act on them.
  useEffect(() => {
    if (viewChosen || !visit) return;
    setViewChosen(true);
    if (flaggedPoints.length > 0 && ((status === 'RETURNED' && isAuditor) || reviewing)) setView('flagged');
  }, [viewChosen, visit, flaggedPoints.length, status, isAuditor, reviewing]);

  const missingEvidence = useMemo(
    () => areas.reduce((n, a) => n + activeQuestions(a).filter((q) => q.answer === 'NO' && q.evidence.length === 0).length, 0),
    [areas]);

  const overall = useMemo(() => {
    let answered = 0;
    let total = 0;
    areas.forEach((a) => { const p = progressOf(a); answered += p.answered; total += p.total; });
    return { answered, total };
  }, [areas]);

  const treeData = useMemo<DataNode[]>(() => {
    const build = (area: Area, closedAbove: boolean): DataNode => {
      const closed = closedAbove || !!area.closed;
      const { answered, total } = progressOf(area);
      const done = total > 0 && answered === total;
      return {
        key: area.id,
        title: (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ color: closed ? '#aaa' : undefined, whiteSpace: 'normal' }}>
              <Text type="secondary" style={{ marginRight: 4 }}>{area.code}</Text>{area.name}
            </span>
            <span style={{ whiteSpace: 'nowrap' }}>
              {closed ? <Tag>Closed</Tag> : done
                ? <CheckCircleFilled style={{ color: '#52c41a' }} />
                : <Text type="secondary">{answered}/{total}</Text>}
            </span>
          </div>
        ),
        children: area.children.filter((c): c is Area => !isQuestion(c)).map((c) => build(c, closed)),
      };
    };
    return areas.map((a) => build(a, false));
  }, [areas]);

  const found = selectedId ? findArea(areas, selectedId) : null;
  const area = found?.area || null;
  const closedAbove = found?.closedAbove || null;
  const closedHere = area?.closed || closedAbove?.closed || null;

  // Inside a sub-domain, points that still need work come first. The order is fixed when the
  // sub-domain is opened, so a card does not jump away while the auditor is answering it.
  const orderedPrimaries = (sub: Area): Question[] => {
    const primaries = sub.children.filter(isQuestion);
    let ids = order.current[sub.id];
    if (!ids) {
      const score = (p: Question) => {
        const group = [p, ...(p.secondaries || [])];
        if (group.some((q) => q.flag?.status === 'OPEN')) return 2;
        return group.some(needsAttention) ? 1 : 0;
      };
      ids = [...primaries].sort((a, b) => score(b) - score(a)).map((p) => p.id);
      order.current[sub.id] = ids;
    }
    return ids.map((id) => primaries.find((p) => p.id === id)).filter((p): p is Question => !!p);
  };

  const saveAnswer = async (q: Question, answer: AnswerValue, remarks: string) => {
    try {
      await putVisitAnswer(visitId, q.id, { answer, remarks });
      setAreas((prev) => patchQuestion(prev, q.id, { answer, remarks, nc: answer === 'NO' ? q.nc : null }));
      if (answer === 'NO' && !q.nc) setNcFor({ ...q, answer, remarks });
    } catch (err) {
      message.error(getApiError(err, 'Could not save the answer.'));
    }
  };

  const sendFlag = async () => {
    if (!flagFor) return;
    try {
      await flagVisitPoint(visitId, flagFor.id, flagNote);
      message.success('Flagged for the auditors.');
      setFlagFor(null);
      load(true);
    } catch (err) {
      message.error(getApiError(err, 'Could not flag this point.'));
    }
  };

  const removeFlag = async (q: Question) => {
    try {
      await removeVisitFlag(visitId, q.id);
      load(true);
    } catch (err) {
      message.error(getApiError(err, 'Could not update the flag.'));
    }
  };

  const address = async (q: Question, reply: string) => {
    try {
      await addressVisitFlag(visitId, q.id, reply);
      message.success('Marked as addressed.');
      load(true);
    } catch (err) {
      message.error(getApiError(err, 'Could not mark this point as addressed.'));
    }
  };

  const undo = async (q: Question) => {
    try {
      await undoAddressVisitFlag(visitId, q.id);
      load(true);
    } catch (err) {
      message.error(getApiError(err, 'Could not take back "addressed".'));
    }
  };

  const uploadEvidence = async (q: Question, file: File) => {
    try {
      const res = await uploadVisitEvidence(visitId, q.id, file);
      setAreas((prev) => patchQuestion(prev, q.id, { evidence: res.data }));
    } catch (err) {
      message.error(getApiError(err, 'Could not upload the file.'));
    }
  };

  const removeEvidence = async (q: Question, evidenceId: string) => {
    try {
      const res = await deleteVisitEvidence(visitId, q.id, evidenceId);
      setAreas((prev) => patchQuestion(prev, q.id, { evidence: res.data }));
    } catch (err) {
      message.error(getApiError(err, 'Could not remove the file.'));
    }
  };

  const reopen = async (target: Area) => {
    try {
      await setVisitScope(visitId, { item_id: target.id, not_applicable: false });
      message.success(`${target.code} reopened.`);
      load(true);
    } catch (err) {
      message.error(getApiError(err, 'Could not reopen this area.'));
    }
  };

  const rowFor = (q: Question, extra: { primaryFailed?: boolean; indent?: boolean; context?: string; parentText?: string }) => (
    <QuestionRow
      key={q.id}
      question={q}
      indent={!!extra.indent}
      primaryFailed={!!extra.primaryFailed}
      disabled={!pointEditable(q)}
      onAnswer={saveAnswer}
      onEditNC={setNcFor}
      canReview={reviewing}
      onReview={setReviewFor}
      canFlag={reviewing}
      onFlag={(question) => { setFlagNote(question.flag?.note || ''); setFlagFor(question); }}
      onRemoveFlag={removeFlag}
      canAddress={status === 'RETURNED' && isAuditor}
      onAddress={address}
      canUndo={status === 'RETURNED' && isAuditor}
      onUndo={undo}
      onHistory={setHistoryFor}
      onUploadEvidence={uploadEvidence}
      onRemoveEvidence={removeEvidence}
      context={extra.context}
      parentText={extra.parentText}
    />
  );

  if (loading) return <Spin style={{ display: 'block', marginTop: 48 }} />;

  const canClose = ((status === 'OPEN' && isLead) || reviewing) && !!area && !closedAbove;
  const flatPoints = view === 'flagged' ? flaggedPoints : attentionPoints;

  const viewOptions = [
    { label: 'By area', value: 'area' },
    ...(showAttention ? [{ label: `Needs attention (${attentionPoints.length})`, value: 'attention' }] : []),
    ...(flaggedPoints.length > 0
      ? [{ label: `Flagged by reviewer (${openFlags} open of ${flaggedPoints.length})`, value: 'flagged' }] : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Visits</Button>
        <Title level={4} style={{ margin: 0 }}>{visit?.visit_id}</Title>
        {visit && <Tag>{visit.status.replace('_', ' ')}</Tag>}
        {!auditorPhase && <Tag color="blue">Read only</Tag>}
        <Button type={auditorPhase ? 'primary' : 'default'} onClick={() => setClosingOpen(true)}>
          {auditorPhase ? 'Meetings and submit' : 'Meetings'}
        </Button>
        {visit && (
          <ReviewActions visitId={visitId} status={visit.status} isReviewer={isReviewer} onChanged={() => load(true)} />
        )}
        {visit?.status === 'COMPLETED' && <Button onClick={() => setResultOpen(true)}>Result</Button>}
        {missingEvidence > 0 && <Tag color="error">{missingEvidence} No answer(s) without evidence</Tag>}
        <div style={{ width: 220 }}>
          <Progress
            percent={overall.total ? Math.round((overall.answered / overall.total) * 100) : 0}
            format={() => `${overall.answered}/${overall.total}`}
            size="small"
          />
        </div>
      </Space>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {visit?.status === 'RETURNED' && visit.remarks && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Returned by the reviewer"
          description={<span style={{ whiteSpace: 'pre-wrap' }}>{visit.remarks}</span>}
        />
      )}

      {viewOptions.length > 1 && (
        <Segmented
          style={{ marginBottom: 12 }} value={view} options={viewOptions}
          onChange={(v) => { setView(v as View); setShown(PAGE); }}
        />
      )}

      {view !== 'area' && (
        <div>
          {status === 'RETURNED' && isAuditor && view === 'flagged' && (
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message="Only the points marked ★ by the reviewer can be changed now. Everything else is locked."
            />
          )}
          {flatPoints.length === 0 && <Empty description="Nothing here." />}
          {flatPoints.slice(0, shown).map(({ q, path, parent }) => rowFor(q, {
            primaryFailed: parent?.answer === 'NO', context: path, parentText: parent?.text,
          }))}
          {flatPoints.length > shown && (
            <Button onClick={() => setShown(shown + PAGE)}>Show {Math.min(PAGE, flatPoints.length - shown)} more</Button>
          )}
        </div>
      )}

      {view === 'area' && (
        <Row gutter={16}>
          <Col xs={24} md={9} lg={8}>
            <div style={{ background: '#fff', padding: 8, borderRadius: 6, maxHeight: '75vh', overflow: 'auto' }}>
              <Tree
                blockNode
                treeData={treeData}
                defaultExpandedKeys={areas.map((a) => a.id)}
                selectedKeys={selectedId ? [selectedId] : []}
                onSelect={(keys) => setSelectedId(keys.length ? String(keys[0]) : null)}
              />
            </div>
          </Col>

          <Col xs={24} md={15} lg={16}>
            {!area && <Empty description="Choose a domain, trade or sub-domain on the left." />}

            {area && (
              <div>
                <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
                  <Title level={5} style={{ margin: 0 }}>{area.code} {area.name}</Title>
                  {canClose && !area.closed && (
                    <Button onClick={() => setScopeFor(area)}>Close area (not audited)</Button>
                  )}
                  {canClose && area.closed && (
                    <Button onClick={() => (reviewing ? setReopenFor(area) : reopen(area))}>Reopen this area</Button>
                  )}
                </Space>

                {closedHere && (
                  <Alert
                    type="warning" showIcon style={{ marginBottom: 12 }}
                    message={`Closed: ${closedHere.reason_label}${closedAbove ? ` (via ${closedAbove.code})` : ''}`}
                    description={closedHere.remarks || undefined}
                  />
                )}

                {!closedHere && area.kind !== 'SUB_DOMAIN' && (
                  <Alert type="info" showIcon message="Choose a sub-domain on the left to answer its questions." />
                )}

                {!closedHere && area.kind === 'SUB_DOMAIN' && orderedPrimaries(area).map((p) => (
                  <React.Fragment key={p.id}>
                    {rowFor(p, {})}
                    {(p.secondaries || []).map((s) => rowFor(s, { indent: true, primaryFailed: p.answer === 'NO' }))}
                  </React.Fragment>
                ))}
              </div>
            )}
          </Col>
        </Row>
      )}

      <NCDrawer
        visitId={visitId}
        question={ncFor}
        setup={ncSetup}
        contractors={contractors}
        onClose={() => setNcFor(null)}
        onSaved={() => { setNcFor(null); load(true); }}
      />
      <ClosingPanel
        visitId={visitId}
        open={closingOpen}
        openingEditable={isAuditor && status === 'OPEN'}
        meetingEditable={auditorPhase && !!visit?.meeting_open}
        auditorPhase={auditorPhase}
        isLead={isLead}
        onClose={() => setClosingOpen(false)}
        onSubmitted={() => { setClosingOpen(false); load(true); }}
      />
      <ScopeModal
        visitId={visitId}
        area={scopeFor || reopenFor}
        reopen={!!reopenFor}
        reviewer={reviewing}
        answeredCount={scopeFor ? questionsUnder(scopeFor).filter((q) => q.answer).length : 0}
        onClose={() => { setScopeFor(null); setReopenFor(null); }}
        onDone={() => { setScopeFor(null); setReopenFor(null); load(true); }}
      />
      <Modal
        title={flagFor ? `Flag ${flagFor.code} for the auditors` : ''}
        open={!!flagFor} onCancel={() => setFlagFor(null)} onOk={sendFlag}
        okText="Flag" okButtonProps={{ disabled: !flagNote.trim() }} destroyOnHidden
      >
        <Input.TextArea
          rows={4} value={flagNote} onChange={(e) => setFlagNote(e.target.value)}
          placeholder="What should the auditors look at or correct?"
        />
      </Modal>
      <ReviewChangeModal
        visitId={visitId}
        question={reviewFor}
        onClose={() => setReviewFor(null)}
        onDone={() => { setReviewFor(null); load(true); }}
      />
      <HistoryDrawer visitId={visitId} question={historyFor} onClose={() => setHistoryFor(null)} />
      <ResultPanel visitId={visitId} open={resultOpen} onClose={() => setResultOpen(false)} />
    </div>
  );
};

export default ChecklistScreen;
